import React from 'react';
import R from 'ramda';

import { Button, Link } from '../../common/components/core';
import ReCaptcha from '../../common/components/core/ReCaptcha';
import EmailInput from './EmailInput';
import PasswordInput from './PasswordInput';
import AuthCheckbox from './AuthCheckbox';
import AuthDropdown from './AuthDropdown';
import AuthInput from './AuthInput';
import LoggedoutActions from '../../main/actions/LoggedoutActions';
import LoggedoutStore from '../../main/stores/LoggedoutStore';
import { guess, getCurrentTimeInUTC } from '../../main/support/dateUtils';
import classNames from 'classnames';
import {
  firstNameValid,
  lastNameValid,
  inputTooLong,
  emailValid,
  passwordsMatch,
  roleSelected,
  termsTickbox
} from './ScreenSignupValidation';

import {
  POLICIES_LINK,
  TERMS_CONSENT_DB_FIELD
} from '../../common/support/gdprConstants';

import { track } from '../../common/support/mixpanel';
import { getCookie } from '../../main/support/utils';

const country = window.App.context.country || {};

export default class ScreenSignup extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      errors: LoggedoutStore.getErrors(),
      name: null,
      last_name: null,
      email: null,
      password: null,
      password2: null,
      role: null,
      acceptedTerms: false,
      marketingOptOut: false,
      lowercase: false,
      capital: false,
      number: false,
      symbol: false,
      min: false,
      passwordMatch: false,
      passwordChecked: false,
      password2Checked: false,
      errorFootnotes: {
        name: null,
        last_name: null,
        email: null,
        password: null,
        password2: null,
        role: null,
        terms: false
      }
    };
    this.validationParams = [
      { title: 'Lowercase Letter', type: 'lowercase', regex: /[a-z]/ },
      { title: 'Capital Letter', type: 'capital', regex: /[A-Z]/ },
      { title: 'Number', type: 'number', regex: /[0-9]/ },
      { title: 'Symbol', type: 'symbol', regex: /[|\\/~^:,;?!&%$@*+#]/ },
      { title: 'Min of 8 characters', type: 'min', regex: /^.{8,}$/ }
    ];
  }

  componentWillMount() {
    window.addEventListener('keypress', this._handleEnterKeyPress);
    LoggedoutStore.addChangeListener(this._onChange);
  }

  componentWillUnmount() {
    window.removeEventListener('keypress', this._handleEnterKeyPress);
    LoggedoutStore.removeChangeListener(this._onChange);
    this.refs.form.removeEventListener('invalid', this._preventDefault, true);
  }

  componentDidMount() {
    LoggedoutActions.clearErrors();
    this.refs.form.addEventListener('invalid', this._preventDefault, true);
  }

  _handleEnterKeyPress = (event: Object) => {
    if (event.keyCode === 13) {
      this._ctaClick();
    }
  };

  _onChange = () => {
    const errors = LoggedoutStore.getErrors();
    this.setState({ errors });
  };

  _handleNameChange = (e, key) => {
    this.setState({ [key]: e.value });
    this._validateNames(key, e.value);
    LoggedoutActions.clearErrors();
  };

  _handlePasswordChange = e => {
    this.setState({ password: e.value });
    this._validatePasswordParams(e.value);
    this._validatePassword(e.value);
    LoggedoutActions.clearErrors();
  };

  _handlePassword2Change = e => {
    this.setState({ password2: e.value });
    this._passwordsMatch(e.target.value);
    LoggedoutActions.clearErrors();
  };

  _handleEmailChange = e => {
    this.setState({ email: e.value });
    this._validateEmail(e.value);
    LoggedoutActions.clearErrors();
  };

  _setReCaptchaResonse = recaptcha_response => {
    this.setState({ recaptcha_response });
  };

  _handleRoleChange = e => {
    this.setState({ role: e.value });
    this._validateRole(e.value);
    LoggedoutActions.clearErrors();
  };

  _handleTermsChange = e => {
    this.setState({
      acceptedTerms: e.target.checked
    });
    this._validateTerms(e.target.checked);
    LoggedoutActions.clearErrors();
  };

  _handleMarketingChange = e => {
    this.setState({ marketingOptOut: e.target.checked });
  };

  /**
   * This will check to see if input fields have a value.
   * userInput is an array which will contain either the name of the rule/input if it is empty,
   * false if it is not empty.
   */
  _emptyFields = () => {
    const userInput = Object.keys(this.state.errorFootnotes).map(rule => {
      if (rule === 'terms') {
        return !this.state.acceptedTerms ? rule : false;
      } else {
        return !this.state[rule] ? rule : false;
      }
    });
    this._setAllErrors(userInput);
    return userInput;
  };

  /**
   * This allows us to set all the errors at once, if a user clicks on the CTA button without
   * filling in any inputs.
   */
  _setAllErrors = userInput => {
    const errorMap = {
      name: 'Please specify a first name.',
      last_name: 'Please specify a last name.',
      email: 'Please specify a valid email address eg. myname@email.com.',
      password: 'Please specify a password.',
      password2: "Passwords don't match.",
      role: 'Please specify a role.',
      terms:
        'Please accept the Terms & Conditions and Privacy Policy to continue.'
    };
    const currentErrors = { ...this.state.errorFootnotes };
    userInput.forEach(field => {
      !!field ? (currentErrors[field] = errorMap[field]) : null;
    });
    this.setState({ errorFootnotes: currentErrors });
  };

  /**
   * This checks the inputs and returns true if every field has an input value.
   */
  _allFieldsFilled = fields => {
    for (let i = 0; i < fields.length; i++) {
      if (fields[i] === null) {
        return false;
      }
    }
    return true;
  };

  _mixpanelTracking = (pageName: string) => {
    track(`Create Account button clicked on ${pageName}`, {
      bmp: getCookie('bmp')
    });
  };

  _ctaClick = () => {
    const data = R.omit(
      ['errors', 'disabledSignUp', 'acceptedTerms'],
      this.state
    );
    data.timezone = guess;
    data[TERMS_CONSENT_DB_FIELD] = this.state.acceptedTerms
      ? getCurrentTimeInUTC()
      : null;
    if (!country.hasDataProtectionPolicy) {
      delete data.marketingOptOut;
    }
    data.country = country.name;

    const formInputs = this._emptyFields();
    if (
      this._allFieldsFilled(formInputs) &&
      this._renderPasswordSuccessIcon(this.validationParams)
    ) {
      this._mixpanelTracking('Sign Up Page');
      LoggedoutActions.signup(data);
    }
  };

  _preventDefault = ev => {
    ev.preventDefault();
  };

  _renderPasswordSuccessIcon = validationParams => {
    let validCount = 0;
    this.validationParams.forEach(rule => {
      const currentRule = rule.type;
      if (!!this.state[currentRule]) {
        validCount += 1;
      }
    });
    return validCount === 5 ? true : false;
  };

  _validatePasswordParams = input => {
    const params = this.validationParams.map(rule => {
      const currentRule = rule.type;
      this.setState({
        [currentRule]:
          !!rule.regex.test(input) && !inputTooLong(input) ? true : false,
        password2Checked: false
      });
      return !!rule.regex.test(input) && !inputTooLong(input) ? true : false;
    });
    return params.every(rule => !!rule);
  };

  _renderPasswordValidation = key => {
    const validationLabels = this.validationParams.map(rule => {
      const currentRule = rule.type;
      return (
        <li key={rule.type} className="validation-li" data-testid={rule.type}>
          {!!this.state[currentRule] ? (
            <i className="material-icons valid">check</i>
          ) : (
            <i className="material-icons invalid">close</i>
          )}
          <span
            className={`validation-label ${classNames({
              ['validation-met']: !!this.state[currentRule]
            })}`}
          >
            {rule.title}
          </span>
        </li>
      );
    });
    /**
     * The password input is the only one whose footer prop doesn't run _renderErrors (to determine frontend errors or backend errors),
     * so this logic is repeated here to cover all scenarios for password input footers.
     * Order of footnote rendering:
     * 1. if user clicks on CTA button without focusing on input field and input field is empty, render errors returned from backend (state.errors)
     * 2. if user has typed anything into the input field, render the validationLabels
     * 3. if user's input does not pass front-end validations, render errors (errorFootnotes)
     * 4. else render the default footer
     */
    if (this.state.errors[key]) {
      return this.state.errors[key];
    } else if (this.state.password) {
      return <ul className="validation-rules">{validationLabels}</ul>;
    } else if (this.state.errorFootnotes[key]) {
      return this.state.errorFootnotes[key];
    } else {
      return (
        <span className="default-password-footer">
          Must contain a capital and lowercase letter, a number, a symbol, and
          min of 8 characters.
        </span>
      );
    }
  };

  _validateNames = (key, input) => {
    const currentErrors = { ...this.state.errorFootnotes };
    if (key === 'name') {
      currentErrors[key] = !firstNameValid(input)
        ? 'Please specify a first name.'
        : inputTooLong(input)
        ? 'Please specify a shorter name.'
        : '';
      this.setState({ errorFootnotes: currentErrors });
    } else if (key === 'last_name') {
      currentErrors[key] = !lastNameValid(input)
        ? 'Please specify a last name.'
        : inputTooLong(input)
        ? 'Please specify a shorter last name.'
        : '';
      this.setState({ errorFootnotes: currentErrors });
    }
  };

  _validateEmail = input => {
    const currentErrors = { ...this.state.errorFootnotes };
    currentErrors.email = !emailValid(input)
      ? 'Please specify a valid email address eg. myname@email.com.'
      : inputTooLong(input)
      ? 'Please specify a shorter email.'
      : '';
    this.setState({ errorFootnotes: currentErrors });
  };

  _validatePassword = input => {
    const currentErrors = { ...this.state.errorFootnotes };
    currentErrors.password = !input
      ? 'Please specify a password.'
      : !this._validatePasswordParams(input)
      ? 'error'
      : '';
    this.setState({
      errorFootnotes: currentErrors,
      passwordChecked: true
    });
  };

  _passwordsMatch = input => {
    const currentErrors = { ...this.state.errorFootnotes };
    let match = false;
    if (!!this._renderPasswordSuccessIcon(this.validationParams)) {
      currentErrors.password2 = !passwordsMatch(this.state.password, input)
        ? "Passwords don't match."
        : '';
      match = !!passwordsMatch(this.state.password, input) ? true : false;
    } else {
      currentErrors.password2 = 'Password invalid.';
    }
    this.setState({
      passwordMatch: match,
      password2Checked: true,
      errorFootnotes: currentErrors
    });
  };

  _validateRole = input => {
    const currentErrors = { ...this.state.errorFootnotes };
    currentErrors.role = !roleSelected(input) ? 'Please specify a role.' : '';
    this.setState({ errorFootnotes: currentErrors });
  };

  _validateTerms = input => {
    const currentErrors = { ...this.state.errorFootnotes };
    currentErrors.terms = !termsTickbox(input)
      ? 'Please accept the Terms & Conditions and Privacy Policy to continue.'
      : '';
    this.setState({ errorFootnotes: currentErrors });
  };

  // this is here to determine which to show in the input footnote; the frontend errors (errorFootnotes) or
  // the errors returned from the backend (this.state.errors) which are generated on CTA button click.
  _renderErrors = key => {
    return this.state.errorFootnotes[key]
      ? this.state.errorFootnotes[key]
      : this.state.errors[key];
  };

  render() {
    const options = [
      { value: 'Marketer', label: 'Marketing' },
      { value: 'Product Manager', label: 'Product' },
      { value: 'Developer', label: 'Engineering' },
      { value: 'Business Intelligence', label: 'Business Intelligence' },
      { value: 'Other', label: 'Other' }
    ];

    return (
      <div className="loggedout--container">
        <img src="/static/images/branch-logos/branch-logo_dark.svg" />
        <div className="signup-container">
          <div className="auth-header">
            <div>
              <h1 className="create-account h_fw-700">Sign Up</h1>
              <Link
                to="/login"
                className="signin-link"
                i13nModel={{ label: 'to Sign in page' }}
                testid="link-to-signin"
              >
                <span className="h_fw-400">Existing user?</span>
                Sign in
              </Link>
            </div>
          </div>
          <form
            role="form"
            className="sign-up-form-1 login-form"
            ref="form"
            onSubmit={this._preventDefault}
          >
            <div className="modal-body">
              <div className="sign-up-container sign-up-1">
                <AuthInput
                  label="First Name"
                  onChange={e => this._handleNameChange(e, 'name')}
                  value={this.state.name}
                  name="name"
                  testid="name"
                  i13nLabel="First Name input"
                  hasError={this._renderErrors('name')}
                  footnote={this._renderErrors('name')}
                />
                <AuthInput
                  label="Last Name"
                  onChange={e => this._handleNameChange(e, 'last_name')}
                  value={this.state.last_name}
                  name="last_name"
                  testid="last_name"
                  i13nLabel="Last Name input"
                  hasError={this._renderErrors('last_name')}
                  footnote={this._renderErrors('last_name')}
                />
                <EmailInput
                  label="Work Email"
                  onChange={this._handleEmailChange}
                  value={this.state.email}
                  name="email"
                  testid="email"
                  i13nLabel="Work Email input"
                  hasError={this._renderErrors('email')}
                  footnote={this._renderErrors('email')}
                />
                <PasswordInput
                  label="Create Password"
                  onChange={this._handlePasswordChange}
                  value={this.state.password}
                  name="password"
                  testid="password-input"
                  i13nLabel="Password input"
                  hasError={this._renderErrors('password')}
                  footnote={this._renderPasswordValidation('password')}
                  inputValid={!this._renderErrors('password')}
                  inputChecked={this.state.passwordChecked}
                  pasteDisabled={true}
                  displayShowHide={true}
                  autoComplete="new-password"
                />
                <PasswordInput
                  label="Confirm Password"
                  onChange={this._handlePassword2Change}
                  value={this.state.password2}
                  name="password2"
                  testid="password2"
                  i13nLabel="Confirm Password input"
                  hasError={this._renderErrors('password2')}
                  footnote={this._renderErrors('password2')}
                  inputValid={this.state.passwordMatch}
                  inputChecked={this.state.password2Checked}
                  autoComplete="new-password"
                />
                <div className="dropdown-terms-container">
                  <AuthDropdown
                    label="Primary Role"
                    testid="signup-role"
                    i13nLabel="Primary Role"
                    onChange={this._handleRoleChange}
                    value={this.state.role}
                    isSearchable={false}
                    clearable={false}
                    placeholder="Primary Role"
                    options={options}
                    outerClassName="auth_dropdown"
                    onBlur={() => this._validateRole(this.state.role)}
                  />
                  <div className="loggedout-error" data-testid="role-error">
                    {this._renderErrors('role')}
                  </div>
                  <AuthCheckbox
                    checked={this.state.acceptedTerms}
                    onChange={this._handleTermsChange}
                    i13nModel={{ label: 'Accept Terms Checkbox' }}
                    className="sign-up-checkbox t-c"
                    label={
                      <span className="h_fw-400">
                        <span>I agree to Branch's </span>
                        <Link
                          href={`${POLICIES_LINK}#terms-and-conditions`}
                          testid="terms-and-conditions-link"
                          i13nModel={{ label: 'Terms & Conditions Link' }}
                        >
                          Terms & Conditions
                        </Link>
                        <span> and </span>
                        <Link
                          href={`${POLICIES_LINK}#privacy`}
                          testid="privacy-policy-link"
                          i13nModel={{ label: 'Privacy Policy link' }}
                        >
                          Privacy Policy
                        </Link>
                        <span>.</span>
                      </span>
                    }
                    id="terms"
                    onBlur={this._validateTerms}
                  />
                  <div
                    className="loggedout-error terms"
                    data-testid="terms-error"
                  >
                    {this._renderErrors('terms')}
                  </div>
                  {country.hasDataProtectionPolicy ? (
                    <AuthCheckbox
                      checked={this.state.marketingOptOut}
                      onChange={this._handleMarketingChange}
                      className="sign-up-checkbox marketing-checkbox"
                      labelClassName="marketing-checkbox__label"
                      i13nModel={{
                        label: 'Accept Marketing Communications Checkbox'
                      }}
                      label={
                        <span className="h_fw-300">
                          By submitting your information, Branch will add you to
                          its email list to keep you posted about Branch
                          products and promotions in accordance with our privacy
                          policy.{' '}
                          <span className="h_fw-400 h_mb-10">
                            If you do not want to be added to that list, please
                            check this box.
                          </span>
                        </span>
                      }
                      id="marketing_communications"
                      tabindex="7"
                    />
                  ) : null}
                </div>
              </div>
              <div className="sign-up__recaptcha">
                <ReCaptcha
                  reset={!!Object.keys(this.state.errors).length}
                  callback={this._setReCaptchaResonse}
                  expiredCallback={this._setReCaptchaResonse}
                  sitekey={window.RECAPTCHA_KEY}
                />
                <div className="sign-up__recaptcha--error">
                  {this.state.errors.recaptcha}
                </div>
              </div>
              <div className="loggedout--sign-up-options h_mb-10">
                <Button
                  onClick={this._ctaClick}
                  className="btn-signup"
                  testid="sign-up"
                  nativeButtonType="submit"
                >
                  Create Account
                </Button>
              </div>
            </div>
          </form>
          <div className="loggedout--footer">
            {!country.hasDataProtectionPolicy ? (
              <div className="sign-up-privacy-notice">
                By clicking "Create Account", you acknowledge that Branch may
                send you information about our products and promotions. For
                information about how to opt-out of our promotional emails,
                click{' '}
                <Link
                  testid="sign-up-privacy-notice"
                  href="https://branch.io/policies/#privacy"
                >
                  here
                </Link>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  }
}
